import { Link } from '@inertiajs/react';
import { Card } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { Book } from '@/types/models';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';

interface RecommendedBookCardProps {
    book: Book;
}

export function RecommendedBookCard({ book }: RecommendedBookCardProps) {
    const { bookUrl } = useTranslatedRoutes();
    
    return (
        <Link
            href={bookUrl(book.slug)}
            className="group block h-full"
        >
            <Card className="h-full hover:shadow-md transition-all duration-200 overflow-hidden p-0 gap-0 flex flex-col">
                {/* Book Cover - 2:3 aspect ratio */}
                <div className="relative aspect-[2/3] bg-muted flex-shrink-0">
                    {(book.cover_image_ai || book.cover_image) ? (
                        <img
                            src={book.cover_image_ai || book.cover_image}
                            alt={book.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full p-4 bg-gradient-to-br from-muted to-muted/50">
                            <p className="text-xs text-muted-foreground text-center line-clamp-3">
                                {book.title}
                            </p>
                        </div>
                    )}
                </div>
                
                {/* Book Info - Fixed height sections */}
                <div className="p-3 flex flex-col flex-grow">
                    {/* Title - fixed height with line-clamp */}
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors min-h-[2.5rem]">
                        {book.title}
                    </h3>
                    
                    {/* Author - fixed height */}
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1 min-h-[1rem]">
                        {book.author}
                    </p>
                    
                    {/* Rating - fixed height container, pushed to bottom */}
                    <div className="mt-auto pt-1 min-h-[1.25rem]">
                        {book.rating_value ? (
                            <div className="flex items-center gap-1">
                                <Star className="h-3 w-3 fill-accent text-accent" />
                                <span className="text-xs font-medium">{book.rating_value}</span>
                                {book.rating_count && (
                                    <span className="text-xs text-muted-foreground">
                                        ({book.rating_count})
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="h-3"></div>
                        )}
                    </div>
                </div>
            </Card>
        </Link>
    );
}